"use client";

import { useState } from "react";
import { Modal, ModalContent, ModalHeader, ModalBody, Button, Input, Textarea, Select, SelectItem } from "@nextui-org/react";
import { Plus } from "lucide-react";

type ChallengeFrequency = "Daily" | "Weekly" | "Monthly" | "Yearly" | "Long-Term";
type ChallengeDifficulty = "Easy" | "Medium" | "Hard";

const categories = [
  "Physical Activity",
  "Mental Health",
  "Nutrition",
  "Preventative Healthcare",
  "Healthy Habits",
  "Miscellaneous",
];

interface CreateChallengeModalProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onCreateChallenge: (challenge: {
    title: string;
    description: string;
    category: string;
    subcategory: string;
    ageGroup: string;
    frequency: ChallengeFrequency;
    difficulty: ChallengeDifficulty;
  }) => void;
}

export function CreateChallengeModal({ onCreateChallenge, open, onOpenChange }: CreateChallengeModalProps) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [ageGroup, setAgeGroup] = useState("All Ages");
  const [frequency, setFrequency] = useState<ChallengeFrequency>("Daily");
  const [difficulty, setDifficulty] = useState<ChallengeDifficulty>("Medium");

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!category) {
      return; // Prevent submission if no category is selected
    }
    onCreateChallenge({
      title,
      description,
      category,
      subcategory,
      ageGroup,
      frequency,
      difficulty,
    });
    if (onOpenChange) onOpenChange(false);
    // Reset form
    setTitle("");
    setDescription("");
    setCategory("");
    setSubcategory("");
    setAgeGroup("All Ages");
    setFrequency("Daily");
    setDifficulty("Medium");
  };

  const handleFrequencyChange = (value: string) => {
    setFrequency(value as ChallengeFrequency);
  };

  const handleDifficultyChange = (value: string) => {
    setDifficulty(value as ChallengeDifficulty);
  };

  return (
    <Modal 
      isOpen={open} 
      onOpenChange={onOpenChange}
      size="2xl"
      className="bg-secondary"
    >
      <ModalContent>
        <ModalHeader className="text-xl">Create Challenge</ModalHeader>
        <ModalBody className="pb-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e?.target?.value)}
                label="Title"
                placeholder="Enter challenge title"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
                required
              />

              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e?.target?.value)}
                label="Description"
                placeholder="Enter challenge description"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
                minRows={3}
                required
              />

              <Select
                label="Category"
                placeholder="Select category"
                value={category}
                onChange={(e) => setCategory(e?.target?.value)}
                variant="bordered"
                classNames={{
                  trigger: "bg-transparent"
                }}
                required
              >
                {Array.isArray(categories) ? categories.map((cat) => (
                  <SelectItem key={cat} value={cat}>
                    {cat}
                  </SelectItem>
                )) : null}
              </Select>

              <Input
                id="subcategory"
                value={subcategory}
                onChange={(e) => setSubcategory(e?.target?.value)}
                label="Subcategory"
                placeholder="Enter subcategory"
                variant="bordered"
                classNames={{
                  input: "bg-transparent"
                }}
                required
              />

              <Select
                label="Age Group"
                placeholder="Select age group"
                value={ageGroup}
                onChange={(e) => setAgeGroup(e?.target?.value)}
                variant="bordered"
                classNames={{
                  trigger: "bg-transparent"
                }}
              >
                <SelectItem key="all" value="All Ages">All Ages</SelectItem>
                <SelectItem key="18" value="18+">18+</SelectItem>
                <SelectItem key="30" value="30+">30+</SelectItem>
                <SelectItem key="50" value="50+">50+</SelectItem>
                <SelectItem key="65" value="65+">65+</SelectItem>
              </Select>

              <Select
                label="Frequency"
                placeholder="Select frequency"
                value={frequency}
                onChange={(e) => handleFrequencyChange(e?.target?.value)}
                variant="bordered"
                classNames={{
                  trigger: "bg-transparent"
                }}
              >
                <SelectItem key="daily" value="Daily">Daily</SelectItem>
                <SelectItem key="weekly" value="Weekly">Weekly</SelectItem>
                <SelectItem key="monthly" value="Monthly">Monthly</SelectItem>
                <SelectItem key="yearly" value="Yearly">Yearly</SelectItem>
                <SelectItem key="long-term" value="Long-Term">Long-Term</SelectItem>
              </Select>

              <Select
                label="Difficulty"
                placeholder="Select difficulty"
                value={difficulty}
                onChange={(e) => handleDifficultyChange(e?.target?.value)}
                variant="bordered"
                classNames={{
                  trigger: "bg-transparent"
                }}
              >
                <SelectItem key="easy" value="Easy">Easy</SelectItem>
                <SelectItem key="medium" value="Medium">Medium</SelectItem>
                <SelectItem key="hard" value="Hard">Hard</SelectItem>
              </Select>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="light"
                onPress={() => onOpenChange?.(false)}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
              >
                Create Challenge
              </Button>
            </div>
          </form>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}